import { ColumnDef } from "@tanstack/react-table";

import { GetInvoiceStatsResponseDto } from "@/types/api/api-types";
import { DataTableColumnHeader } from "@/components/ui/customs/data-table/data-table-column-header";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

type CompanyStats = GetInvoiceStatsResponseDto["companies"][0];

export const useInvoiceStatsColumns = (): ColumnDef<CompanyStats>[] => {
  const { t } = useTranslation("boInvoices");
  return useMemo(
    () => [
      {
        accessorKey: "businessName",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("stats.columns.client")} />
        ),
        cell: ({ row }) => {
          if (row.depth === 1) {
            return <p className="pl-8 text-gray-500">{(row.original as any).companyName}</p>;
          }
          return (
            <div className="flex items-center gap-2">
              {row.getCanExpand() ? (
                <button onClick={row.getToggleExpandedHandler()} style={{ cursor: "pointer" }}>
                  {row.getIsExpanded() ? <ChevronDown /> : <ChevronRight />}
                </button>
              ) : (
                <div className="w-6" />
              )}
              <div>{row.getValue("businessName")}</div>
            </div>
          );
        },
        enableSorting: false,
        enableHiding: false,
      },
      {
        accessorKey: "amountToBill",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("stats.columns.total")} />
        ),
        cell: ({ row }) => (
          <div className="w-[80px]">
            {(((row.getValue("amountToBill") as number) || 0) / 100).toLocaleString("fr-FR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
            <span className="text-xs text-gray-500"> €</span>
          </div>
        ),
        enableSorting: false,
        enableHiding: false,
      },
      {
        accessorKey: "totalHT",
        id: "Total HT",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("stats.columns.totalHt")} />
        ),
        cell: ({ row }) => (
          <div className="w-[80px]">
            {(((row.getValue("Total HT") as number) || 0) / 100).toLocaleString("fr-FR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
            <span className="text-xs text-gray-500"> €</span>
          </div>
        ),
        enableSorting: false,
        enableHiding: false,
      },
      {
        accessorKey: "totalTva",
        id: "Total TVA",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("stats.columns.totalTva")} />
        ),
        cell: ({ row }) => (
          <div className="w-[80px]">
            {(((row.getValue("Total TVA") as number) || 0) / 100).toLocaleString("fr-FR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
            <span className="text-xs text-gray-500"> €</span>
          </div>
        ),
        enableSorting: false,
      },
      {
        accessorKey: "adjustment",
        id: "Ajustement",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("stats.columns.adjustment")} />
        ),
        cell: ({ row }) => (
          <div className="w-[80px]">
            {(((row.getValue("Ajustement") as number) || 0) / 100).toLocaleString("fr-FR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
            <span className="text-xs text-gray-500"> €</span>
          </div>
        ),
        enableSorting: false,
      },
    ],
    [t],
  );
};
